import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { faPen as faPenSolid } from '@fortawesome/pro-solid-svg-icons';
import { CreateButton } from 'components/button/CreateButton';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { Detail } from 'components/detail/detail';
import { DocumentTitle } from 'components/DocumentTitle';
import { DetailsPage } from 'components/page/DetailsPage';
import { useNotificationContext } from 'contexts/NotificationContext';
import { invalidateThirdPartySensorConfigsDataSource } from 'custom_hooks/data_hooks/data_source/thirdPartySensorConfigsDataSource';
import { useThirdPartySensorDetails } from 'custom_hooks/data_hooks/data_source/useThirdPartySensorDetails';
import { CreateConfigurationModal } from 'pages/Settings/ThirdPartySensorModels/CreateConfigurationModal';
import { EditThirdPartySensorModal } from 'pages/Settings/ThirdPartySensorModels/EditThirdPartySensorModal';
import { SensorConfigsTable } from 'pages/Settings/ThirdPartySensorModels/SensorConfigsTable';
import {
    Columns,
    CreateButtonContainer,
    DetailsContainer,
    Heading,
} from 'pages/Settings/ThirdPartySensorModels/styled';
import { SensorStandardFormatLookup } from 'pages/Settings/ThirdPartySensorModels/ThirdPartySensorsTable';
import { useCreateConfiguration } from 'pages/Settings/ThirdPartySensorModels/useCreateConfiguration';
import { useState } from 'react';
import { DebugValue } from 'shared/debug/DebugValue';
import { useThemeValues } from 'styles/theme';

type Action = 'editDetails' | 'createConfiguration';

export type ThirdPartySensorDetailsPageProps = Readonly<{
    sensorModelId: string;
    corporationId?: string;
}>;

export function ThirdPartySensorDetailsPage({
    sensorModelId,
    corporationId,
}: ThirdPartySensorDetailsPageProps) {
    const { link } = useThemeValues();
    const { showToast } = useNotificationContext();

    const [action, setAction] = useState<Action>();

    const { data: sensorModel, isLoading, error, refetch } = useThirdPartySensorDetails(
        sensorModelId
    );

    const onClose = () => setAction(undefined);

    const onEditClose = (updated?: boolean) => {
        if (updated) {
            refetch();
        }
        onClose();
    };

    const { mutate: createConfiguration, isPending: isSaving } = useCreateConfiguration(
        sensorModelId,
        corporationId,
        {
            onSuccess: () => {
                showToast({
                    type: 'success',
                    content: 'Successfully created sensor configuration',
                });
                onClose();
            },
            onError: () =>
                showToast({
                    type: 'error',
                    content: 'Failed to create sensor configuration',
                }),
            onSettled: () => {
                invalidateThirdPartySensorConfigsDataSource();
            },
        }
    );

    return (
        <>
            <DocumentTitle
                title={
                    sensorModel
                        ? `${sensorModel.Manufacturer.Name} ${sensorModel.ModelNumber} Details`
                        : 'Third Party Sensor Details'
                }
            />
            <DetailsPage
                error={error ? 'Unable to load sensor model' : undefined}
                invalidURL={!sensorModel && !isLoading}
                isLoading={isLoading}
                fullHeight
                fullWidth
            >
                {sensorModel && (
                    <>
                        <DetailsContainer>
                            <Columns>
                                <Detail name="Manufacturer Name">
                                    {sensorModel.Manufacturer.Name}{' '}
                                    <DebugValue
                                        description="Third-Party Sensor"
                                        value={sensorModel}
                                    />
                                </Detail>
                                <Detail name="Standard">
                                    {
                                        SensorStandardFormatLookup[
                                            sensorModel.SensorStandard
                                        ]
                                    }
                                </Detail>
                                <Detail name="Model Number">
                                    {sensorModel.ModelNumber}
                                </Detail>
                                <div className="flex">
                                    <Detail
                                        name="Notes"
                                        className="word-wrap-anywhere align-center"
                                    >
                                        {sensorModel.Notes}
                                    </Detail>
                                    <HoverIconButton
                                        className="ml-10 flex align-start"
                                        icon={faPen}
                                        hoverIcon={faPenSolid}
                                        color={link}
                                        onClick={() => setAction('editDetails')}
                                    />
                                </div>
                            </Columns>
                        </DetailsContainer>
                        <CreateButtonContainer>
                            <Heading>Configurations</Heading>
                            {!sensorModel.Corporation && (
                                <CreateButton
                                    onClick={() => setAction('createConfiguration')}
                                    title="Create Configuration"
                                    ariaLabel="Create Configuration"
                                />
                            )}
                        </CreateButtonContainer>

                        <SensorConfigsTable physicalSensorModel={sensorModel} />

                        {action === 'createConfiguration' && (
                            <CreateConfigurationModal
                                sensorModelId={sensorModel.Id}
                                sensorStandard={sensorModel.SensorStandard}
                                onSave={createConfiguration}
                                isSaving={isSaving}
                                onClose={onClose}
                            />
                        )}
                        {action === 'editDetails' && (
                            <EditThirdPartySensorModal
                                physicalSensorModel={sensorModel}
                                onClose={onEditClose}
                            />
                        )}
                    </>
                )}
            </DetailsPage>
        </>
    );
}
